/**
 * AI Scribe session - survives route changes so the floater can keep running
 * after leaving the encounter. Wired to Azure Speech STT + Azure OpenAI note generation.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import * as platformBillingApi from "@/api/platform-billing.api";
import { useAuth } from "@/hooks/use-auth";
import {
  extractOrders,
  generateAVS,
  labelTranscript,
  suggestBilling,
  updateScribeSessionStatus,
  type CDSAlert,
  type ExtractOrdersResponse,
  type GenerateBHNoteResponse,
  type SafetyHit,
  type ScribeSessionRecord,
  type SuggestBillingResponse,
} from "@/api/ai.api";
import type { AIChartContext, AITemplateType, ScribeNoteSections } from "@/lib/ai-chart-context";
import { detectSafetyRisk } from "@/lib/safety-detect";
import { useScribeWebSocket } from "@/hooks/use-scribe-websocket";
import { withAIAttestation } from "@/lib/ai-attestation";

export type ScribeSOAPSections = ScribeNoteSections;

export type ScribeView = "expanded" | "minimized";
export type ScribePhase =
  | "ready"
  | "countdown"
  | "listening"
  | "paused"
  | "processing"
  | "preview"
  | "dictate";

export interface ScribeSessionMeta {
  encounterId: string;
  patientId?: string;
  patientName?: string;
  chiefComplaint?: string;
  templateType?: AITemplateType;
  chartContext?: AIChartContext;
}

interface PendingApply {
  encounterId: string;
  templateType: AITemplateType;
  sections: ScribeSOAPSections;
}

interface AiScribeContextValue {
  active: boolean;
  view: ScribeView;
  phase: ScribePhase;
  countdown: number;
  elapsed: number;
  processStep: number;
  transcript: string;
  sections: ScribeSOAPSections;
  dictateText: string;
  meta: ScribeSessionMeta | null;
  pendingApply: PendingApply | null;
  source: GenerateBHNoteResponse["source"] | null;
  uncertainties: string[];
  cdsAlerts: CDSAlert[];
  safetyAlerts: SafetyHit[];
  billing: SuggestBillingResponse | null;
  orders: ExtractOrdersResponse | null;
  insightsLoading: boolean;
  avsSummary: string;
  avsLoading: boolean;
  labeledTranscript: string;
  labelLoading: boolean;
  consentGiven: boolean;
  micPermission: "unknown" | "granted" | "denied" | "unsupported";
  speechReady: boolean;
  sttMode: "azure" | "none";
  /** "reconnecting" while the speech connection is down mid-visit. */
  speechStatus: "live" | "reconnecting";
  voiceDetected: boolean;
  connectionState: "idle" | "connecting" | "connected" | "error";
  audioLevel: number;
  errorMessage: string;
  setConsentGiven: (value: boolean) => void;
  startSession: (meta: ScribeSessionMeta) => void;
  minimize: () => void;
  expand: () => void;
  beginCountdown: () => void;
  pause: () => void;
  resume: () => void;
  cancelRestart: () => void;
  finishProcess: () => Promise<void>;
  openDictate: () => void;
  setDictateText: (text: string | ((prev: string) => string)) => void;
  generateFromDictate: () => Promise<void>;
  applyToNote: () => void;
  clearPendingApply: () => void;
  endSession: () => void;
  listenAgain: () => void;
  updateSection: (key: string, value: string) => void;
  generateAvsSummary: () => Promise<void>;
  labelSpeakers: () => Promise<void>;
  restoreSession: (record: ScribeSessionRecord) => void;
}

const AiScribeContext = createContext<AiScribeContextValue | null>(null);

export function useAiScribe() {
  const ctx = useContext(AiScribeContext);
  if (!ctx) throw new Error("useAiScribe must be used within AiScribeProvider");
  return ctx;
}

export function useAiScribeOptional() {
  return useContext(AiScribeContext);
}

export function AiScribeProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // The clinic's AI wording comes with the workspace, which every role can
  // read (most cannot read the organization record).
  const { data: workspace } = useQuery({
    queryKey: ["planWorkspace"],
    queryFn: () => platformBillingApi.getPlanWorkspace(),
    enabled: !!user,
    staleTime: 5 * 60_000,
    retry: false,
  });
  // Visible AI line on applied drafts is a clinic opt-in (off by default).
  const showAIAttestation = Boolean(workspace?.ai_note_attestation_visible);
  // The AI line on patient summaries is on unless the clinic turned it off.
  const showAVSDisclosure = workspace?.ai_avs_disclosure_visible !== false;
  const [active, setActive] = useState(false);
  const [view, setView] = useState<ScribeView>("expanded");
  const [phase, setPhase] = useState<ScribePhase>("ready");
  const [countdown, setCountdown] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const [processStep, setProcessStep] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [sections, setSections] = useState<ScribeSOAPSections>({});
  const [dictateText, setDictateText] = useState("");
  const [meta, setMeta] = useState<ScribeSessionMeta | null>(null);
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null);
  const [source, setSource] = useState<GenerateBHNoteResponse["source"] | null>(null);
  const [uncertainties, setUncertainties] = useState<string[]>([]);
  const [cdsAlerts, setCdsAlerts] = useState<CDSAlert[]>([]);
  const [safetyAlerts, setSafetyAlerts] = useState<SafetyHit[]>([]);
  const [billing, setBilling] = useState<SuggestBillingResponse | null>(null);
  const [orders, setOrders] = useState<ExtractOrdersResponse | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [avsSummary, setAvsSummary] = useState("");
  const [avsLoading, setAvsLoading] = useState(false);
  const [labeledTranscript, setLabeledTranscript] = useState("");
  const [labelLoading, setLabelLoading] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const addSafetyAlert = useCallback((hit: SafetyHit) => {
    setSafetyAlerts((prev) =>
      prev.some((a) => a.category === hit.category) ? prev : [...prev, hit],
    );
  }, []);

  const timerRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);
  const processGen = useRef(0);
  const pausedRef = useRef(false);
  /** Server scribe session for the current draft, and whether it reached review / was applied. */
  const scribeSessionIdRef = useRef<string | null>(null);
  const draftReadyRef = useRef(false);
  const draftAppliedRef = useRef(false);

  /** A draft the clinician reviewed but never applied is recorded as discarded. */
  const settleDraft = useCallback((status: "applied" | "discarded") => {
    const id = scribeSessionIdRef.current;
    const ready = draftReadyRef.current;
    scribeSessionIdRef.current = null;
    draftReadyRef.current = false;
    if (!id || !ready) return;
    if (status === "discarded" && draftAppliedRef.current) return;
    void updateScribeSessionStatus(id, status).catch(() => {
      /* audit trail is best-effort; never block the clinician */
    });
  }, []);

  const scribe = useScribeWebSocket({
    templateType: meta?.templateType ?? "soap",
    encounterId: meta?.encounterId,
    patientId: meta?.patientId,
    chiefComplaint: meta?.chiefComplaint,
    chartContext: meta?.chartContext,
    consentAt: consentGiven ? new Date().toISOString() : undefined,
    onTranscriptDelta: (full) => {
      setTranscript(full);
      // Client-side safety scan - instant, works even when transcription
      // never touches the server mid-visit (browser STT mode).
      for (const hit of detectSafetyRisk(full)) {
        addSafetyAlert(hit);
      }
    },
    onNoteSynthesized: (response) => {
      setSections(response.sections ?? {});
      setSource(response.source);
      setUncertainties(response.uncertainties ?? []);
    },
    onCDSAlerts: setCdsAlerts,
    onSafetyAlert: addSafetyAlert,
    onError: (message) => setErrorMessage(message),
    onSessionStarted: (id) => {
      scribeSessionIdRef.current = id;
      draftReadyRef.current = false;
      draftAppliedRef.current = false;
    },
  });

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      window.clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (phase !== "listening" || pausedRef.current) {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = window.setInterval(() => {
      setElapsed((s) => s + 1);
    }, 1000);
    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [phase]);

  const resetContent = useCallback(() => {
    setElapsed(0);
    setProcessStep(0);
    setTranscript("");
    setSections({});
    setDictateText("");
    setCountdown(3);
    setSource(null);
    setUncertainties([]);
    setCdsAlerts([]);
    setSafetyAlerts([]);
    setBilling(null);
    setOrders(null);
    setInsightsLoading(false);
    setAvsSummary("");
    setAvsLoading(false);
    setLabeledTranscript("");
    setLabelLoading(false);
    setErrorMessage("");
    pausedRef.current = false;
  }, []);

  /** Fire billing + order extraction in the background once a draft exists. */
  const loadInsights = useCallback(
    (noteSections: ScribeSOAPSections, transcriptText: string, sessionMeta: ScribeSessionMeta | null) => {
      const gen = processGen.current;
      setBilling(null);
      setOrders(null);
      setInsightsLoading(true);
      const req = {
        encounter_id: sessionMeta?.encounterId,
        template_type: sessionMeta?.templateType ?? "soap",
        sections: noteSections,
        transcript: transcriptText,
        patient_context: sessionMeta?.chartContext,
      };
      void Promise.allSettled([suggestBilling(req), extractOrders(req)]).then(
        ([billingResult, ordersResult]) => {
          if (gen !== processGen.current) return;
          if (billingResult.status === "fulfilled") setBilling(billingResult.value);
          if (ordersResult.status === "fulfilled") setOrders(ordersResult.value);
          setInsightsLoading(false);
        },
      );
    },
    [],
  );

  const startSession = useCallback(
    (next: ScribeSessionMeta) => {
      settleDraft("discarded");
      clearTimers();
      processGen.current += 1;
      scribe.closeSocket();
      scribe.stopListening();
      scribe.setRecoverySession(null);
      setMeta(next);
      setActive(true);
      setView("expanded");
      setPhase("ready");
      setConsentGiven(false);
      resetContent();
    },
    [clearTimers, resetContent, scribe, settleDraft],
  );

  const endSession = useCallback(() => {
    settleDraft("discarded");
    clearTimers();
    processGen.current += 1;
    scribe.stopListening();
    scribe.closeSocket();
    scribe.setRecoverySession(null);
    setActive(false);
    setView("expanded");
    setPhase("ready");
    setMeta(null);
    setConsentGiven(false);
    resetContent();
  }, [clearTimers, resetContent, scribe, settleDraft]);

  const beginCountdown = useCallback(() => {
    if (!consentGiven) {
      setErrorMessage("Please confirm patient notification before starting ambient capture.");
      return;
    }
    clearTimers();
    setCountdown(3);
    setPhase("countdown");
    let n = 3;
    countdownRef.current = window.setInterval(() => {
      n -= 1;
      if (n <= 0) {
        if (countdownRef.current) {
          window.clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        setCountdown(0);
        setElapsed(0);
        setPhase("listening");
        pausedRef.current = false;
        void scribe.startListening().catch(() => {
          setPhase("ready");
        });
        return;
      }
      setCountdown(n);
    }, 1000);
  }, [clearTimers, consentGiven, scribe]);

  const pause = useCallback(() => {
    if (phase === "listening") {
      pausedRef.current = true;
      void scribe.pauseListening();
      setPhase("paused");
    }
  }, [phase, scribe]);

  const resume = useCallback(() => {
    if (phase === "paused") {
      pausedRef.current = false;
      setPhase("listening");
      void scribe.resumeListening().catch(() => {
        setPhase("paused");
      });
    }
  }, [phase, scribe]);

  const cancelRestart = useCallback(() => {
    settleDraft("discarded");
    clearTimers();
    processGen.current += 1;
    scribe.stopListening();
    scribe.closeSocket();
    scribe.setRecoverySession(null);
    resetContent();
    setPhase("ready");
    setView("expanded");
  }, [clearTimers, resetContent, scribe, settleDraft]);

  const synthesizeAndPreview = useCallback(
    async (promptText: string) => {
      const gen = ++processGen.current;
      clearTimers();
      setPhase("processing");
      setProcessStep(0);
      setView("expanded");
      setErrorMessage("");

      // Step 0 → 1: wrap up capture, then generate note
      await new Promise((r) => window.setTimeout(r, 400));
      if (gen !== processGen.current) return;
      setProcessStep(1);

      const started = Date.now();
      const response = await scribe.synthesizeNote(promptText);
      const elapsedMs = Date.now() - started;
      if (elapsedMs < 1200) {
        await new Promise((r) => window.setTimeout(r, 1200 - elapsedMs));
      }
      if (gen !== processGen.current) return;

      if (!response) {
        setDictateText(promptText);
        setErrorMessage(
          (prev) =>
            prev ||
            "Could not generate a clinical note. Paste or dictate your visit summary below, then click Generate.",
        );
        setPhase("dictate");
        return;
      }

      setTranscript(promptText);
      setSections(response.sections ?? {});
      setSource(response.source);
      setUncertainties(response.uncertainties ?? []);
      setProcessStep(2);
      await scribe.runCDS(response.sections ?? {});
      if (gen !== processGen.current) return;
      setProcessStep(3);
      await new Promise((r) => window.setTimeout(r, 350));
      if (gen !== processGen.current) return;
      draftReadyRef.current = true;
      setPhase("preview");
      loadInsights(response.sections ?? {}, promptText, meta);
    },
    [clearTimers, loadInsights, meta, scribe],
  );

  const finishProcess = useCallback(async () => {
    const gen = ++processGen.current;
    clearTimers();
    setPhase("processing");
    setProcessStep(0);
    setView("expanded");
    setErrorMessage("");

    await new Promise((r) => window.setTimeout(r, 400));
    if (gen !== processGen.current) return;
    setProcessStep(1);

    const started = Date.now();
    const response = await scribe.finishAndSynthesize();
    const elapsedMs = Date.now() - started;
    if (elapsedMs < 1200) {
      await new Promise((r) => window.setTimeout(r, 1200 - elapsedMs));
    }
    if (gen !== processGen.current) return;

    const promptText = scribe.transcript.trim() || transcript.trim();

    if (!response) {
      setProcessStep(0);
      setDictateText(promptText);
      setErrorMessage(
        (prev) =>
          prev ||
          "No speech was captured. Check your microphone, speak for at least 5 seconds, then try again - or type/paste notes below.",
      );
      setPhase("dictate");
      return;
    }

    setProcessStep(2);
    setTranscript(promptText || scribe.transcript);
    setSections(response.sections ?? {});
    setSource(response.source);
    setUncertainties(response.uncertainties ?? []);
    await scribe.runCDS(response.sections ?? {});
    if (gen !== processGen.current) return;
    setProcessStep(3);
    await new Promise((r) => window.setTimeout(r, 350));
    if (gen !== processGen.current) return;
    draftReadyRef.current = true;
    setPhase("preview");
    loadInsights(response.sections ?? {}, promptText || scribe.transcript, meta);
  }, [clearTimers, loadInsights, meta, scribe, transcript]);

  const openDictate = useCallback(() => {
    clearTimers();
    scribe.stopListening();
    // Close the live socket so Dictate generation goes through REST with
    // exactly the text typed here - never mixed with an old live session.
    scribe.closeSocket();
    setPhase("dictate");
    setView("expanded");
  }, [clearTimers, scribe]);

  const generateFromDictate = useCallback(async () => {
    const text = dictateText.trim();
    if (!text) {
      setErrorMessage("Enter or dictate encounter notes before generating.");
      return;
    }
    await synthesizeAndPreview(text);
  }, [dictateText, synthesizeAndPreview]);

  const applyToNote = useCallback(() => {
    if (!meta?.encounterId || !sections) return;
    setPendingApply({
      encounterId: meta.encounterId,
      templateType: meta.templateType ?? "soap",
      sections: showAIAttestation ? withAIAttestation(sections) : { ...sections },
    });
    draftAppliedRef.current = true;
    settleDraft("applied");
    navigate(`/encounters/${meta.encounterId}`);
    setView("minimized");
  }, [meta, navigate, sections, settleDraft, showAIAttestation]);

  const clearPendingApply = useCallback(() => {
    setPendingApply(null);
  }, []);

  const updateSection = useCallback((key: string, value: string) => {
    setSections((prev) => ({ ...prev, [key]: value }));
  }, []);

  const generateAvsSummary = useCallback(async () => {
    if (!Object.keys(sections).length) return;
    setAvsLoading(true);
    try {
      const result = await generateAVS({
        encounter_id: meta?.encounterId,
        template_type: meta?.templateType ?? "soap",
        sections,
        patient_context: meta?.chartContext,
      });
      setAvsSummary(
        showAVSDisclosure && result.disclosure ? `${result.summary}\n\n${result.disclosure}` : result.summary,
      );
    } catch {
      setErrorMessage("Could not generate the patient summary. Try again.");
    } finally {
      setAvsLoading(false);
    }
  }, [meta, sections, showAVSDisclosure]);

  const labelSpeakers = useCallback(async () => {
    const text = transcript.trim();
    if (!text) return;
    setLabelLoading(true);
    try {
      const result = await labelTranscript(text);
      setLabeledTranscript(result.labeled_transcript);
    } catch {
      setErrorMessage("Could not label speakers. Try again.");
    } finally {
      setLabelLoading(false);
    }
  }, [transcript]);

  /** Load a persisted scribe session (draft recovery) straight into preview. */
  const restoreSession = useCallback(
    (record: ScribeSessionRecord) => {
      if (!record.sections_json) {
        // An unfinished visit (closed tab, crash, failed draft): bring the
        // saved transcript back so the clinician can generate from it.
        settleDraft("discarded");
        clearTimers();
        processGen.current += 1;
        scribe.stopListening();
        scribe.closeSocket();
        resetContent();
        // The draft lands on this session, and applying it marks it applied.
        scribe.setRecoverySession(record.id);
        scribeSessionIdRef.current = record.id;
        draftReadyRef.current = false;
        draftAppliedRef.current = false;
        setDictateText(record.transcript ?? "");
        setTranscript(record.transcript ?? "");
        setPhase("dictate");
        setView("expanded");
        return;
      }
      let restoredSections: ScribeSOAPSections = {};
      try {
        restoredSections = record.sections_json ? JSON.parse(record.sections_json) : {};
      } catch {
        restoredSections = {};
      }
      clearTimers();
      processGen.current += 1;
      scribe.stopListening();
      scribe.closeSocket();
      scribe.setRecoverySession(null);
      scribeSessionIdRef.current = record.id;
      draftReadyRef.current = record.status !== "applied";
      draftAppliedRef.current = false;
      setTranscript(record.transcript ?? "");
      setSections(restoredSections);
      setSource((record.source as GenerateBHNoteResponse["source"]) || null);
      setUncertainties([]);
      setCdsAlerts([]);
      setSafetyAlerts([]);
      setAvsSummary("");
      setLabeledTranscript("");
      setErrorMessage("");
      setPhase("preview");
      setView("expanded");
      loadInsights(restoredSections, record.transcript ?? "", meta);
    },
    [clearTimers, loadInsights, meta, resetContent, scribe, settleDraft],
  );

  const listenAgain = useCallback(() => {
    settleDraft("discarded");
    clearTimers();
    processGen.current += 1;
    scribe.stopListening();
    scribe.closeSocket();
    setProcessStep(0);
    setTranscript("");
    setSections({});
    setDictateText("");
    setCountdown(3);
    setElapsed(0);
    setSource(null);
    setUncertainties([]);
    setCdsAlerts([]);
    setErrorMessage("");
    setPhase("ready");
    setView("expanded");
  }, [clearTimers, scribe, settleDraft]);

  const value = useMemo<AiScribeContextValue>(
    () => ({
      active,
      view,
      phase,
      countdown,
      elapsed,
      processStep,
      transcript,
      sections,
      dictateText,
      meta,
      pendingApply,
      source,
      uncertainties,
      cdsAlerts,
      safetyAlerts,
      billing,
      orders,
      insightsLoading,
      avsSummary,
      avsLoading,
      labeledTranscript,
      labelLoading,
      consentGiven,
      micPermission: scribe.micPermission,
      speechReady: scribe.speechReady,
      sttMode: scribe.sttMode,
      speechStatus: scribe.speechStatus,
      voiceDetected: scribe.voiceDetected,
      connectionState: scribe.connectionState,
      audioLevel: scribe.audioLevel,
      errorMessage,
      setConsentGiven,
      startSession,
      minimize: () => setView("minimized"),
      expand: () => setView("expanded"),
      beginCountdown,
      pause,
      resume,
      cancelRestart,
      finishProcess,
      openDictate,
      setDictateText,
      generateFromDictate,
      applyToNote,
      clearPendingApply,
      endSession,
      listenAgain,
      updateSection,
      generateAvsSummary,
      labelSpeakers,
      restoreSession,
    }),
    [
      active,
      view,
      phase,
      countdown,
      elapsed,
      processStep,
      transcript,
      sections,
      dictateText,
      meta,
      pendingApply,
      source,
      uncertainties,
      cdsAlerts,
      safetyAlerts,
      billing,
      orders,
      insightsLoading,
      avsSummary,
      avsLoading,
      labeledTranscript,
      labelLoading,
      consentGiven,
      scribe.micPermission,
      scribe.speechReady,
      scribe.sttMode,
      scribe.speechStatus,
      scribe.voiceDetected,
      scribe.connectionState,
      scribe.audioLevel,
      errorMessage,
      startSession,
      beginCountdown,
      pause,
      resume,
      cancelRestart,
      finishProcess,
      openDictate,
      generateFromDictate,
      applyToNote,
      clearPendingApply,
      endSession,
      listenAgain,
      updateSection,
      generateAvsSummary,
      labelSpeakers,
      restoreSession,
    ],
  );

  return <AiScribeContext.Provider value={value}>{children}</AiScribeContext.Provider>;
}
